import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { evaluationService, AddressEvaluation } from '../services/evaluationService';
import toast from 'react-hot-toast';
import SizeDashboard from '../components/dashboards/SizeDashboard';
import EconomyDashboard from '../components/dashboards/EconomyDashboard';
import CrimeDashboard from '../components/dashboards/CrimeDashboard';
import ResidentsDashboard from '../components/dashboards/ResidentsDashboard';
import RealEstateDashboard from '../components/dashboards/RealEstateDashboard';
import EquityDashboard from '../components/dashboards/EquityDashboard';
import TeamBuildingDashboard from '../components/dashboards/TeamBuildingDashboard';
import SwingsDashboard from '../components/dashboards/SwingsDashboard';
import AppSidebar from '../components/AppSidebar';
import AddressMap from '../components/Maps/AddressMap';
import AddressAutocomplete, { formatAddressForDisplay } from '../components/AddressAutocomplete';
import WtiLoader from '../components/WtiLoader';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFilePdf, faBookmark, faSpinner } from '@fortawesome/free-solid-svg-icons';
import { getLimitErrorMessage } from '../utils/limitErrorMessages';
import '../App.css';

const DASHBOARD_COMPONENTS = {
  size: SizeDashboard,
  economy: EconomyDashboard,
  crime: CrimeDashboard,
  residents: ResidentsDashboard,
  realEstate: RealEstateDashboard,
  equity: EquityDashboard,
  teamBuilding: TeamBuildingDashboard,
  swings: SwingsDashboard,
};

const DASHBOARD_NAMES = {
  size: 'S - SIZE (Market Size & Scale)',
  economy: 'E - ECONOMY (Economic Diversity)',
  crime: 'C - CRIME (Crime & Safety)',
  residents: 'R - RESIDENTS (Demographics & Stability)',
  realEstate: 'R - REAL ESTATE (Housing Market Fundamentals)',
  equity: 'E - EQUITY & CASH FLOW',
  teamBuilding: 'T - TEAM BUILDING (Investor Ecosystem)',
  swings: 'S - SWINGS & TRENDS',
} as const;

const DASHBOARD_SHORT_LABELS: Record<keyof typeof DASHBOARD_NAMES, string> = {
  size: 'Size',
  economy: 'Economy',
  crime: 'Crime',
  residents: 'Residents',
  realEstate: 'Real Estate',
  equity: 'Equity',
  teamBuilding: 'Team',
  swings: 'Swings',
};

const CHOROPLETH_METRIC_LABELS: Record<keyof typeof DASHBOARD_NAMES, string> = {
  size: 'Population',
  economy: 'Unemployment rate',
  crime: 'Crime risk index',
  residents: 'Median household income',
  realEstate: 'Median home value',
  equity: 'Gross yield',
  teamBuilding: 'Market activity',
  swings: 'Vacancy rate',
};

const DASHBOARD_COLORS: Record<keyof typeof DASHBOARD_NAMES, string> = {
  size: '#1d4ed8',
  economy: '#16a34a',
  crime: '#dc2626',
  residents: '#7c3aed',
  realEstate: '#0d9488',
  equity: '#f97316',
  teamBuilding: '#a855f7',
  swings: '#22c55e',
};

const EvaluationDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [evaluation, setEvaluation] = useState<AddressEvaluation | null>(null);
  const evaluationRef = useRef<AddressEvaluation | null>(null);
  const [loading, setLoading] = useState(true);
  const [downloadingReport, setDownloadingReport] = useState(false);
  const [activeDashboard, setActiveDashboard] = useState<string>('size');
  const [headerAddress, setHeaderAddress] = useState<string>('');
  const dashboardFetchInFlightRef = useRef<Set<string>>(new Set());
  const choroplethFetchedRef = useRef<Set<string>>(new Set());
  const rentcastDashboardFetchedRef = useRef<Set<string>>(new Set());

  const loadEvaluation = useCallback(async () => {
    if (!id) return;
    try {
      const data = await evaluationService.getEvaluation(id);

      // For completed evaluations, load only the Size dashboard first.
      // Other dashboards are loaded lazily when you click their tabs.
      if (data.status === 'completed') {
        try {
          const sizeDashboard = await evaluationService.getDashboard(id, 'size');
          const dashboards = { ...(data.dashboards || {}), size: sizeDashboard as any };

          // Populate metroAreaName fallback used in header
          const sizeAny: any = dashboards.size;
          const existingMetroName: string | undefined = sizeAny.metroAreaName;
          const sizeMetroFromApi: string | undefined =
            typeof sizeAny.metroArea === 'string' ? sizeAny.metroArea : undefined;
          const metroAreaName = existingMetroName || sizeMetroFromApi;
          if (metroAreaName) {
            dashboards.size = {
              ...dashboards.size,
              metroAreaName,
            };
          }

          if ((sizeDashboard as any)?.choroplethGeoJson) {
            choroplethFetchedRef.current.add('size');
          }
          setEvaluation({ ...data, dashboards });
          return;
        } catch {
          // fall through to set base evaluation
        }
      }

      setEvaluation(data);
    } catch (error) {
      toast.error(getLimitErrorMessage(error, 'Failed to load evaluation'));
      navigate('/dashboard');
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);
  
  useEffect(() => {
    evaluationRef.current = evaluation;
  }, [evaluation]);

  useEffect(() => {
    dashboardFetchInFlightRef.current.clear();
    choroplethFetchedRef.current.clear();
    rentcastDashboardFetchedRef.current.clear();
  }, [id]);

  useEffect(() => {
    if (!id) return;
    loadEvaluation();
    // Poll while processing, but stop once completed/failed
    const interval = setInterval(() => {
      const current = evaluationRef.current;
      if (!current || current.status === 'processing') {
        loadEvaluation();
      } else {
        clearInterval(interval);
      }
    }, 5000);
    return () => clearInterval(interval);
  }, [id, loadEvaluation]);

  useEffect(() => {
    const addr = evaluation?.address;
    if (!addr?.fullAddress) return;
    setHeaderAddress(
      formatAddressForDisplay(addr.fullAddress, {
        street: addr.street,
        city: addr.city,
        state: addr.state,
        zipCode: addr.zipCode,
      }),
    );
  }, [
    evaluation?.address?.fullAddress,
    evaluation?.address?.street,
    evaluation?.address?.city,
    evaluation?.address?.state,
    evaluation?.address?.zipCode,
  ]);

  const handleEvaluateFromHeader = async (overrideAddress?: string) => {
    const addressToUse = (overrideAddress ?? headerAddress).trim();
    if (!addressToUse) {
      toast.error('Please enter an address');
      return;
    }
    try {
      const newEvaluation = await evaluationService.evaluateAddress(
        addressToUse,
        evaluation?.propertyType || 'single_family'
      );
      toast.success('Evaluation started!');
      navigate(`/evaluation/${newEvaluation._id}`);
    } catch (error: any) {
      toast.error(getLimitErrorMessage(error, 'Failed to evaluate address'));
    }
  };

  const handleToggleSave = async () => {
    if (!evaluation) return;
    try {
      if (evaluation.isSaved) {
        const updated = await evaluationService.unsaveEvaluation(evaluation._id);
        setEvaluation(updated);
        toast.success('Report removed from saved list');
      } else {
        const updated = await evaluationService.saveEvaluation(evaluation._id);
        setEvaluation(updated);
        toast.success('Report saved');
      }
    } catch (error) {
      toast.error(getLimitErrorMessage(error, 'Failed to update saved report'));
    }
  };

  const handleGenerateReport = async () => {
    if (!id || downloadingReport) return;
    setDownloadingReport(true);
    try {
      const blob = await evaluationService.generateReport(id);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `WTI_Report_${id}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('Report downloaded');
    } catch (error) {
      toast.error(getLimitErrorMessage(error, 'Failed to generate report'));
    } finally {
      setDownloadingReport(false);
    }
  };

  // Choropleth GeoJSON is not stored in Mongo (size limit) — fetch on demand per tab.
  useEffect(() => {
    if (!evaluation?._id || evaluation.status !== 'completed') return;
    // Equity tab uses a dedicated enrich fetch below (avoids duplicate /equity calls).
    if (activeDashboard === 'equity') return;

    const existingDashboard = evaluation.dashboards?.[
      activeDashboard as keyof typeof evaluation.dashboards
    ] as any;

    const metroAsOf = existingDashboard?.metroEmploymentGrowth?.asOfPeriod;
    const metroAsOfDate = metroAsOf && /^(\d{4})-(\d{2})$/.test(metroAsOf)
      ? new Date(Number(metroAsOf.slice(0, 4)), Number(metroAsOf.slice(5, 7)) - 1, 1)
      : null;
    const metroStale =
      metroAsOfDate != null &&
      metroAsOfDate < new Date(new Date().setMonth(new Date().getMonth() - 4));

    const incomeYears = (existingDashboard?.incomeTrend?.years || [])
      .map((y: any) => Number(y))
      .filter((y: number) => Number.isFinite(y));
    const incomeLatest = incomeYears.length ? Math.max(...incomeYears) : null;
    const incomeStale = incomeLatest == null || incomeLatest < new Date().getFullYear() - 2;

    const trendHasData = (trend: any) =>
      Boolean(
        trend?.years?.length &&
          Array.isArray(trend?.datasets) &&
          trend.datasets.some(
            (ds: any) => Array.isArray(ds?.data) && ds.data.some((v: any) => Number(v) > 0),
          ),
      );

    const needsSwingsEnrichment =
      activeDashboard === 'swings' &&
      (!existingDashboard?.metroEmploymentGrowth ||
        !existingDashboard?.neighborhoodEmploymentGrowth ||
        metroStale ||
        incomeStale ||
        !trendHasData(existingDashboard?.ownerValueTrend) ||
        !trendHasData(existingDashboard?.renterRentTrend));

    if (existingDashboard?.choroplethGeoJson && !needsSwingsEnrichment) {
      choroplethFetchedRef.current.add(activeDashboard);
      return;
    }

    if (needsSwingsEnrichment) {
      choroplethFetchedRef.current.delete(activeDashboard);
    }

    if (choroplethFetchedRef.current.has(activeDashboard)) return;
    if (dashboardFetchInFlightRef.current.has(activeDashboard)) return;

    const apiKey =
      activeDashboard === 'realEstate'
        ? 'realestate'
        : activeDashboard === 'teamBuilding'
          ? 'teambuilding'
          : activeDashboard;

    dashboardFetchInFlightRef.current.add(activeDashboard);
    (async () => {
      try {
        const dashboardData = await evaluationService.getDashboard(evaluation._id, apiKey);
        const resData = dashboardData as any;
        const incomeYears = (resData?.incomeTrend?.years || [])
          .map((y: any) => Number(y))
          .filter((y: number) => Number.isFinite(y));
        const incomeLatest = incomeYears.length ? Math.max(...incomeYears) : null;
        const swingsComplete =
          activeDashboard !== 'swings' ||
          (resData?.metroEmploymentGrowth &&
            resData?.neighborhoodEmploymentGrowth &&
            incomeLatest != null &&
            incomeLatest >= new Date().getFullYear() - 2 &&
            trendHasData(resData?.ownerValueTrend) &&
            trendHasData(resData?.renterRentTrend));
        // Do not treat choropleth alone as complete for Swings — deferred jobs/value/rent
        // must arrive or we keep allowing retries on tab revisit.
        if (activeDashboard === 'swings' ? swingsComplete : resData?.choroplethGeoJson) {
          choroplethFetchedRef.current.add(activeDashboard);
        }
        setEvaluation((prev) => {
          if (!prev) return prev;
          const dashboards = { ...(prev.dashboards || {}), [activeDashboard]: dashboardData as any };
          return { ...prev, dashboards };
        });
      } catch {
        // allow retry when user switches tabs
      } finally {
        dashboardFetchInFlightRef.current.delete(activeDashboard);
      }
    })();
  }, [activeDashboard, evaluation?._id, evaluation?.status]);

  // Equity uses live Rentcast property + AVM data (not stored fully in Mongo).
  useEffect(() => {
    if (!evaluation?._id || evaluation.status !== 'completed') return;
    if (activeDashboard !== 'equity') return;
    if (dashboardFetchInFlightRef.current.has('equity')) return;
    const equityDash = evaluation.dashboards?.equity as {
      cashFlowStatement?: unknown;
      propertyInfo?: { bedrooms?: number; bathrooms?: number; squareFootage?: number };
    } | undefined;
    const propertyInfoComplete =
      equityDash?.propertyInfo?.bedrooms != null &&
      equityDash?.propertyInfo?.bathrooms != null &&
      equityDash?.propertyInfo?.squareFootage != null;
    if (
      rentcastDashboardFetchedRef.current.has('equity') &&
      equityDash?.cashFlowStatement &&
      propertyInfoComplete
    ) {
      return;
    }

    dashboardFetchInFlightRef.current.add('equity');
    (async () => {
      try {
        const equityDashboard = await evaluationService.getDashboard(evaluation._id, 'equity');
        if (!equityDashboard || typeof equityDashboard !== 'object') return;
        setEvaluation((prev) => {
          if (!prev) return prev;
          return {
            ...prev,
            dashboards: { ...(prev.dashboards || {}), equity: equityDashboard as any },
          };
        });
        const enriched = equityDashboard as {
          cashFlowStatement?: unknown;
          propertyInfo?: { bedrooms?: number; bathrooms?: number; squareFootage?: number };
        };
        const done =
          Boolean(enriched?.cashFlowStatement) &&
          enriched?.propertyInfo?.bedrooms != null &&
          enriched?.propertyInfo?.bathrooms != null &&
          enriched?.propertyInfo?.squareFootage != null;
        if (done) {
          rentcastDashboardFetchedRef.current.add('equity');
        }
      } catch (error: any) {
        rentcastDashboardFetchedRef.current.delete('equity');
        if (error?.response?.status === 429) {
          toast.error('Too many requests. Wait a moment, then switch tabs and back to Equity.');
        }
      } finally {
        dashboardFetchInFlightRef.current.delete('equity');
      }
    })();
  }, [activeDashboard, evaluation?._id, evaluation?.status]);

  if (loading) {
    return <WtiLoader fullPage message="Loading evaluation…" />;
  }

  if (!evaluation) {
    return <div className="loading">Evaluation not found</div>;
  }

  const ActiveDashboardComponent = DASHBOARD_COMPONENTS[activeDashboard as keyof typeof DASHBOARD_COMPONENTS];
  const sizeDashboard = evaluation.dashboards?.size as any;
  const activeDashboardData =
    evaluation.dashboards?.[activeDashboard as keyof typeof evaluation.dashboards] as any;

  // Prefer backend-provided value property for the active dashboard.
  // Fallback to known defaults per tab so each dashboard gets its own value-based layers.
  const activeChoroplethValueProperty: string =
    activeDashboardData?.choroplethValueProperty ||
    (activeDashboard === 'size'
      ? 'population'
      : activeDashboard === 'economy'
        ? 'unemploymentRate'
        : activeDashboard === 'crime'
          ? 'crimeRiskProxy'
          : activeDashboard === 'residents'
            ? 'medianHouseholdIncome'
            : activeDashboard === 'realEstate'
              ? 'censusMedianHomeValue'
              : activeDashboard === 'equity'
                ? 'grossYieldProxy'
                : activeDashboard === 'teamBuilding'
                  ? 'teamBuildingActivityProxy'
                  : activeDashboard === 'swings'
                    ? 'vacancyRateProxy'
                    : 'population');

  // IMPORTANT: keep each dashboard's own heatmap layer.
  // Only Size falls back to its own choropleth if needed.
  const choroplethGeoJson =
    activeDashboardData?.choroplethGeoJson ||
    (activeDashboard === 'size' ? sizeDashboard?.choroplethGeoJson : undefined);

  const choroplethValueProperty: string = activeChoroplethValueProperty;

  const locationLabels = (sizeDashboard?.locationLabels || {}) as {
    neighborhood?: string;
    city?: string;
    county?: string;
    metroArea?: string;
    state?: string;
  };

  const isCensusTractPlaceLabel = (label?: string | null) => {
    const s = String(label || '').trim();
    if (!s) return false;
    return /^census\s+tract\b/i.test(s) || /^tract\s+[\d.]+$/i.test(s);
  };

  const poiNeighborhood =
    evaluation.address.neighborhood && !isCensusTractPlaceLabel(evaluation.address.neighborhood)
      ? evaluation.address.neighborhood
      : null;
  const displayNeighborhood =
    [poiNeighborhood, locationLabels.neighborhood].find(
      (v) => v && !isCensusTractPlaceLabel(v),
    ) || '—';

  const metroAreaName =
    (evaluation.address as any).metroArea ||
    locationLabels.metroArea ||
    sizeDashboard?.metroAreaName ||
    '—';

  return (
    <div className="wti-shell">
      <AppSidebar />

      <div className="wti-main">
        <header className="wti-header">
          <div className="wti-header-left">
            <div className="wti-search-row">
              <div style={{ width: '65%' }}>
                <AddressAutocomplete
                  value={headerAddress}
                  onChange={(val) => setHeaderAddress(val)}
                  onAddressSelect={(addr, place) => {
                    setHeaderAddress(addr);
                    handleEvaluateFromHeader(addr);
                  }}
                  placeholder="Search address"
                />
              </div>
            </div>
          </div>
          <div className="wti-header-right">
            {evaluation.status === 'completed' && (
              <>
                <button
                  type="button"
                  className={`btn btn-ghost wti-save-btn ${evaluation.isSaved ? 'wti-save-btn--saved' : ''}`}
                  onClick={handleToggleSave}
                  title={evaluation.isSaved ? 'Remove from saved' : 'Save report'}
                >
                  <FontAwesomeIcon icon={faBookmark} style={{ marginRight: 8 }} />
                  <span style={{ fontWeight: 600 }}>
                    {evaluation.isSaved ? 'Saved' : 'Save report'}
                  </span>
                </button>
                <button
                  type="button"
                  className="btn btn-success wti-download-btn"
                  onClick={handleGenerateReport}
                  disabled={downloadingReport}
                  aria-busy={downloadingReport}
                >
                  <FontAwesomeIcon
                    icon={downloadingReport ? faSpinner : faFilePdf}
                    spin={downloadingReport}
                    style={{ marginRight: 8 }}
                  />
                  <span style={{ fontWeight: 600 }}>
                    {downloadingReport ? 'Downloading…' : 'Download report'}
                  </span>
                </button>
              </>
            )}
          </div>
        </header>

        {evaluation.status === 'processing' && (
          <div className="wti-processing-banner">
            Evaluation is processing. This may take a few moments...
          </div>
        )}

        {evaluation.status === 'completed' && (
          <main className="wti-content">
            <section className="wti-map-column">
              <AddressMap
                address={{
                  ...evaluation.address,
                  choroplethGeoJson,
                  choroplethValueProperty,
                  choroplethLabel: DASHBOARD_SHORT_LABELS[
                    activeDashboard as keyof typeof DASHBOARD_SHORT_LABELS
                  ],
                  choroplethMetricLabel: CHOROPLETH_METRIC_LABELS[
                    activeDashboard as keyof typeof CHOROPLETH_METRIC_LABELS
                  ],
                }}
                neighborhoodTractCode={sizeDashboard?.geography?.tractCode}
                evaluationNeighborhood={evaluation.address.neighborhood}
                locationContext={{
                  city: evaluation.address.city,
                  county: evaluation.address.county,
                  state: evaluation.address.state,
                }}
              />
            </section>

            <section className="wti-right-column">
              <div className="wti-right-fixed-header">
                <div className="wti-location-summary">
                <div>
                  <p className="wti-location-label">Neighborhood</p>
                  <p className="wti-location-value">
                    {displayNeighborhood || '—'}
                  </p>
                </div>
                <div>
                  <p className="wti-location-label">City</p>
                  <p className="wti-location-value">
                    {evaluation.address.city || locationLabels.city || '—'}
                  </p>
                </div>
                <div>
                  <p className="wti-location-label">County</p>
                  <p className="wti-location-value">
                    {evaluation.address.county ||
                      locationLabels.county ||
                      sizeDashboard?.geography?.county ||
                      '—'}
                  </p>
                </div>
                <div>
                  <p className="wti-location-label">Metro area</p>
                  <p className="wti-location-value">{metroAreaName}</p>
                </div>
                <div>
                  <p className="wti-location-label">State</p>
                  <p className="wti-location-value">{evaluation.address.state || '—'}</p>
                </div>
                </div>

                <div className="wti-dashboard-switcher">
                {(Object.keys(DASHBOARD_NAMES) as Array<keyof typeof DASHBOARD_NAMES>).map(
                  (key) => {
                    const isActive = activeDashboard === key;
                    const fullLabel = DASHBOARD_NAMES[key];
                    const circleLetter = fullLabel.charAt(0);
                    const shortLabel = DASHBOARD_SHORT_LABELS[key];
                    const color = DASHBOARD_COLORS[key];

                    return (
                      <button
                        key={key}
                        type="button"
                        title={fullLabel}
                        className={`wti-dashboard-icon ${
                          isActive ? 'wti-dashboard-icon--active' : ''
                        }`}
                        onClick={() => setActiveDashboard(key)}
                      >
                        <span
                          className="wti-dashboard-circle"
                          style={{ borderColor: color, color: isActive ? color : '#111827' }}
                        >
                          {circleLetter}
                        </span>
                        {isActive && (
                          <span className="wti-dashboard-label">
                            {shortLabel}
                          </span>
                        )}
                      </button>
                    );
                  }
                )}
                </div>
              </div>

              <div className="wti-dashboard-panel card">    
                {evaluation.dashboards[activeDashboard as keyof typeof evaluation.dashboards] ? (
                  <ActiveDashboardComponent
                    data={evaluation.dashboards[activeDashboard as keyof typeof evaluation.dashboards]}
                    address={{
                      ...evaluation.address,
                      metroArea:
                        metroAreaName !== '—'
                          ? metroAreaName
                          : (evaluation.address as any)?.metroArea,
                    }}
                    {...(activeDashboard === 'equity'
                      ? { evaluationId: evaluation._id }
                      : {})}
                  />
                ) : (
                  <p>Dashboard data not available</p>
                )}
              </div>
            </section>
          </main>
        )}
      </div>
    </div>
  );
};

export default EvaluationDetail;
          